import React from "react";
import { Box, Stack, Tooltip } from "@mui/joy";
import type { ZekayCustomer } from "../../../../api/customers/Customer.typed";
import {
  SOpenProfile,
  SDelete,
  SProduct,
  SCalendarMonth,
  SSchool,
} from "../../../../styles/StyledMUI";
import { useConfirmationModal } from "../../../../contexts/app/confirmation";
import { useCustomer } from "../../../../contexts/api";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import {
  emojiFontFamily,
  gradientListRowCardShellSx,
  listRowAccentPlainSx,
  listRowDangerPlainSx,
} from "../../../../styles/globalStyles";
import { normalizeCustomerEmoji } from "../../../common/customerEmoji";
import CustomerProductsModal from "../../../shared/customerProducts/CustomerProductsModal";
import CustomerExcursionsModal from "../../../shared/customerExcursions/CustomerExcursionsModal";
import CustomerTrainingsModal from "../../../shared/customerTrainings/CustomerTrainingsModal";

import { useCustomersModulePermissions } from "../../hooks/useCustomersModulePermissions";
import { useCustomersData } from "../../../../contexts/app/customersData";

interface CustomersItemProps {
  customer: ZekayCustomer | undefined;
  onOpenProfile: (customer: ZekayCustomer) => void;
  desyncMode?: boolean;
  onDelete?: (customer: ZekayCustomer) => void;
}

export default function CustomerItem(props: CustomersItemProps) {
  const { customer, onOpenProfile, desyncMode, onDelete } = props;
  const { getCustomer, getCustomerExcursions } = useCustomer();
  const { updateCustomerInList } = useCustomersData();
  const { showConfirmation } = useConfirmationModal();
  const {
    canOpenProfile,
    canOpenTrainingsModal,
    canOpenExcursionsModal,
    canOpenCustomerProducts,
    canDeleteCustomer,
  } = useCustomersModulePermissions(Boolean(desyncMode));

  const [productsOpen, setProductsOpen] = React.useState(false);
  const [excursionsOpen, setExcursionsOpen] = React.useState(false);
  const [trainingsOpen, setTrainingsOpen] = React.useState(false);
  const [excursionCount, setExcursionCount] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (!canOpenExcursionsModal || !customer?.Id) return;
    getCustomerExcursions(customer.Id, 1, 1).then((res) => {
      setExcursionCount(res?.total ?? 0);
    });
  }, [customer?.Id, canOpenExcursionsModal, getCustomerExcursions]);

  const customerName = customer
    ? [customer.FirstName, customer.LastName].filter(Boolean).join(" ").trim()
    : undefined;

  const qualification = (customer?.LastQualification ?? "").trim();

  const customerEmoji = normalizeCustomerEmoji(customer?.Emoji);

  const handleClickOpenProfile = React.useCallback(() => {
    if (customer && customer.Id) {
      getCustomer(String(customer.Id)).then((r) => {
        const customerToShow = r || customer;
        if (customerToShow) onOpenProfile(customerToShow);
      });
    } else if (customer) {
      onOpenProfile(customer);
    }
  }, [customer, getCustomer, onOpenProfile]);

  const refreshCustomerRowFromApi = React.useCallback(async () => {
    if (customer?.Id == null) return;
    const full = await getCustomer(String(customer.Id));
    if (full) updateCustomerInList(full);
  }, [customer?.Id, getCustomer, updateCustomerInList]);

  const handleDelete = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!customer || !onDelete) return;
      const ok = await showConfirmation(
        "Supprimer le client",
        "Êtes-vous sûr de vouloir supprimer ce client ?",
      );
      if (ok) onDelete(customer);
    },
    [customer, onDelete, showConfirmation],
  );

  return (
    <Box sx={gradientListRowCardShellSx}>
      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
              flex: 1,
            }}
          >
            {canOpenProfile && (
              <Tooltip title="Profil">
                <IconButtonCTA
                  ctaVariant="accent"
                  size="sm"
                  variant="plain"
                  onClick={handleClickOpenProfile}
                  aria-label="Profil"
                  sx={{ ...listRowAccentPlainSx, flexShrink: 0 }}
                >
                  <SOpenProfile />
                </IconButtonCTA>
              </Tooltip>
            )}
            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
              <DarkTypography
                level="title-md"
                colorVariant="primary"
                sx={{
                  fontWeight: 700,
                  minWidth: 0,
                  overflow: "hidden",
                  display: { xs: "-webkit-box", sm: "block" },
                  WebkitBoxOrient: { xs: "vertical" },
                  WebkitLineClamp: { xs: 2 },
                  wordBreak: { xs: "break-word" },
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  textOverflow: { sm: "ellipsis" },
                  lineHeight: { xs: 1.25, sm: 1.2 },
                }}
              >
                {customerName || "—"}
                {customerEmoji ? (
                  <Box component="span" sx={{ ml: 0.75, fontFamily: emojiFontFamily }} aria-hidden>
                    {customerEmoji}
                  </Box>
                ) : null}
              </DarkTypography>
              {qualification && (
                <DarkTypography
                  level="body-xs"
                  colorVariant="muted"
                  sx={{
                    overflow: "hidden",
                    display: { xs: "-webkit-box", sm: "block" },
                    WebkitBoxOrient: { xs: "vertical" },
                    WebkitLineClamp: { xs: 2 },
                    wordBreak: { xs: "break-word" },
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    textOverflow: { sm: "ellipsis" },
                    lineHeight: { xs: 1.3, sm: 1.2 },
                  }}
                >
                  {qualification}
                </DarkTypography>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexShrink: 0,
              alignItems: "center",
              ml: "auto",
            }}
          >
            {canOpenTrainingsModal && customer?.Id != null && (
              <Tooltip title={customer.HasActiveTrainings ? "Formations" : "Aucune formation"}>
                <span>
                  <IconButtonCTA
                    ctaVariant="accent"
                    size="sm"
                    variant="plain"
                    disabled={!customer.HasActiveTrainings}
                    onClick={() => setTrainingsOpen(true)}
                    aria-label="Formations"
                    sx={listRowAccentPlainSx}
                  >
                    <SSchool />
                  </IconButtonCTA>
                </span>
              </Tooltip>
            )}

            {canOpenExcursionsModal && customer?.Id != null && (
              <Tooltip title={excursionCount === 0 ? "Aucune sortie" : "Sorties"}>
                <span>
                  <IconButtonCTA
                    ctaVariant="accent"
                    size="sm"
                    variant="plain"
                    disabled={excursionCount === 0}
                    onClick={() => setExcursionsOpen(true)}
                    aria-label="Sorties"
                    sx={listRowAccentPlainSx}
                  >
                    <SCalendarMonth />
                  </IconButtonCTA>
                </span>
              </Tooltip>
            )}

            {canOpenCustomerProducts && customer?.Id != null && (
              <Tooltip title="Produits">
                <IconButtonCTA
                  ctaVariant="accent"
                  size="sm"
                  variant="plain"
                  onClick={() => setProductsOpen(true)}
                  aria-label="Produits"
                  sx={listRowAccentPlainSx}
                >
                  <SProduct />
                </IconButtonCTA>
              </Tooltip>
            )}

            {canDeleteCustomer && onDelete && customer?.Id != null && (
              <Tooltip title="Supprimer">
                <IconButtonCTA
                  ctaVariant="danger"
                  size="sm"
                  variant="plain"
                  onClick={handleDelete}
                  aria-label="Supprimer le client"
                  sx={listRowDangerPlainSx}
                >
                  <SDelete />
                </IconButtonCTA>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {trainingsOpen && canOpenTrainingsModal && customer?.Id != null && (
        <CustomerTrainingsModal
          open={trainingsOpen}
          onClose={() => setTrainingsOpen(false)}
          customerId={customer.Id!}
          customerName={customerName}
        />
      )}

      {productsOpen && canOpenCustomerProducts && customer?.Id != null && (
        <CustomerProductsModal
          open={productsOpen}
          onClose={() => setProductsOpen(false)}
          customerId={customer.Id!}
          customerName={customerName}
          onCustomerUpdated={refreshCustomerRowFromApi}
        />
      )}

      {excursionsOpen && canOpenExcursionsModal && customer?.Id != null && (
        <CustomerExcursionsModal
          open={excursionsOpen}
          onClose={() => setExcursionsOpen(false)}
          customerId={customer.Id!}
          customerName={customerName}
        />
      )}
    </Box>
  );
}
